import type { AssignmentDTO, EventShowDTO, RoleFeeKind } from "../contracts";
import { ASSIGNMENT_LINE_KEY_REHEARSAL } from "./assignment-line-key";

export type ShowMatrixAssignment = {
  id: string;
  performerId: string;
  performerName: string;
  roleFeeKind: RoleFeeKind;
  lineKey: string;
  eventShowIds: string[];
};

export type ShowMatrixColumn = {
  showId: string;
  label: string;
  showDate: Date;
  sortOrder: number;
};

export type ShowMatrixCell = {
  showId: string;
  linked: boolean;
};

export type ShowMatrixRow = {
  performerId: string;
  performerName: string;
  assignments: ShowMatrixAssignment[];
  cells: ShowMatrixCell[];
};

export type ShowAssignmentMatrixModel = {
  columns: ShowMatrixColumn[];
  rows: ShowMatrixRow[];
};

export type ShowMatrixBulkUpdateItem = {
  assignmentId: string;
  eventShowIds: string[];
};

export type PartialShowParticipationRow = {
  performerId: string;
  performerName: string;
  selectedCount: number;
  totalShowCount: number;
  eventShowIds: string[];
};

function isShowMatrixAssignment(assignment: {
  lineKey: string;
  rehearsalDays: number;
  showCount: number;
}): boolean {
  if (assignment.lineKey === ASSIGNMENT_LINE_KEY_REHEARSAL) return false;
  if (assignment.rehearsalDays > 0 && assignment.showCount === 0) return false;
  return true;
}

function showDateMs(showDate: Date | string): number {
  return new Date(showDate).getTime();
}

function sortShows(shows: EventShowDTO[]): EventShowDTO[] {
  return [...shows].sort(
    (a, b) =>
      showDateMs(a.showDate) - showDateMs(b.showDate) ||
      a.sortOrder - b.sortOrder,
  );
}

export function isPerformerLinkedToShow(
  assignments: Pick<ShowMatrixAssignment, "eventShowIds">[],
  showId: string,
): boolean {
  return assignments.some((assignment) =>
    assignment.eventShowIds.includes(showId),
  );
}

export function toggleShowForAssignment(
  assignment: Pick<ShowMatrixAssignment, "roleFeeKind" | "eventShowIds">,
  showId: string,
  linked: boolean,
): string[] | null {
  const current = [...assignment.eventShowIds];

  if (linked) {
    if (assignment.roleFeeKind === "music_arranger") {
      return [showId];
    }
    if (current.includes(showId)) return current;
    return [...current, showId];
  }

  if (!current.includes(showId)) return current;
  const next = current.filter((id) => id !== showId);
  return next.length > 0 ? next : null;
}

export function toggleShowForPerformer(
  assignments: ShowMatrixAssignment[],
  showId: string,
  linked: boolean,
): ShowMatrixBulkUpdateItem[] {
  const updates: ShowMatrixBulkUpdateItem[] = [];

  for (const assignment of assignments) {
    const next = toggleShowForAssignment(assignment, showId, linked);
    if (next == null) continue;
    const changed =
      next.length !== assignment.eventShowIds.length ||
      next.some((id, index) => id !== assignment.eventShowIds[index]);
    if (!changed) continue;
    updates.push({ assignmentId: assignment.id, eventShowIds: next });
  }

  return updates;
}

export function buildShowAssignmentMatrix(
  assignments: AssignmentDTO[],
  eventShows: EventShowDTO[],
): ShowAssignmentMatrixModel {
  const sortedShows = sortShows(eventShows);
  const columns: ShowMatrixColumn[] = sortedShows.map((show, index) => ({
    showId: show.id,
    label: `ໂຊ ${index + 1}`,
    showDate: show.showDate,
    sortOrder: show.sortOrder,
  }));

  const byPerformer = new Map<string, ShowMatrixAssignment[]>();
  for (const assignment of assignments) {
    if (!isShowMatrixAssignment(assignment)) continue;
    const list = byPerformer.get(assignment.performerId) ?? [];
    list.push({
      id: assignment.id,
      performerId: assignment.performerId,
      performerName: assignment.performerName ?? assignment.performerId,
      roleFeeKind: assignment.roleFeeKind,
      lineKey: assignment.lineKey,
      eventShowIds: assignment.eventShowIds ?? [],
    });
    byPerformer.set(assignment.performerId, list);
  }

  const rows: ShowMatrixRow[] = [...byPerformer.entries()]
    .map(([performerId, performerAssignments]) => {
      const performerName =
        performerAssignments[0]?.performerName ?? performerId;
      return {
        performerId,
        performerName,
        assignments: performerAssignments,
        cells: columns.map((column) => ({
          showId: column.showId,
          linked: isPerformerLinkedToShow(
            performerAssignments,
            column.showId,
          ),
        })),
      };
    })
    .sort((a, b) => a.performerName.localeCompare(b.performerName));

  return { columns, rows };
}

export function listPartialShowParticipation(
  assignments: AssignmentDTO[],
  totalShowCount: number,
): PartialShowParticipationRow[] {
  if (totalShowCount < 2) return [];

  const byPerformer = new Map<
    string,
    { performerName: string; showIds: Set<string> }
  >();

  for (const assignment of assignments) {
    if (!isShowMatrixAssignment(assignment)) continue;
    const entry = byPerformer.get(assignment.performerId) ?? {
      performerName: assignment.performerName ?? assignment.performerId,
      showIds: new Set<string>(),
    };
    for (const showId of assignment.eventShowIds ?? []) {
      entry.showIds.add(showId);
    }
    byPerformer.set(assignment.performerId, entry);
  }

  return [...byPerformer.entries()]
    .map(([performerId, entry]) => ({
      performerId,
      performerName: entry.performerName,
      selectedCount: entry.showIds.size,
      totalShowCount,
      eventShowIds: [...entry.showIds],
    }))
    .filter(
      (row) => row.selectedCount > 0 && row.selectedCount < totalShowCount,
    )
    .sort((a, b) => a.performerName.localeCompare(b.performerName));
}

export function buildShowRosterRows(
  assignments: AssignmentDTO[],
  eventShows: EventShowDTO[],
  showId?: string,
): Array<{
  showId: string;
  showLabel: string;
  showDate: Date;
  performers: Array<{ performerId: string; performerName: string; roles: string[] }>;
}> {
  const sortedShows = sortShows(eventShows);
  const targetShows = showId
    ? sortedShows.filter((show) => show.id === showId)
    : sortedShows;

  return targetShows.map((show, index) => {
    const performers = new Map<string, { name: string; roles: Set<string> }>();
    for (const assignment of assignments) {
      if (!(assignment.eventShowIds ?? []).includes(show.id)) continue;
      const entry = performers.get(assignment.performerId) ?? {
        name: assignment.performerName ?? assignment.performerId,
        roles: new Set<string>(),
      };
      entry.roles.add(assignment.roleFeeKind);
      performers.set(assignment.performerId, entry);
    }

    return {
      showId: show.id,
      showLabel: `ໂຊ ${sortedShows.findIndex((item) => item.id === show.id) + 1 || index + 1}`,
      showDate: show.showDate,
      performers: [...performers.entries()]
        .map(([performerId, entry]) => ({
          performerId,
          performerName: entry.name,
          roles: [...entry.roles],
        }))
        .sort((a, b) => a.performerName.localeCompare(b.performerName)),
    };
  });
}
